import {
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  CAROUSEL_INVALID_ACTIVE_ID,
  CAROUSEL_NO_ELEMENTS,
} from '../../constants/constants';
import { Utils } from '../../utils/utils';
import {
  CarouselNavigation,
  FluidCarouselProps,
} from './components/fluid-carousel.components';

/**
 * @displayName Carousel
 *
 * @contextData The Carousel is a slideshow for cycling through a series of content.
 * It works with a series of images, text, or custom markup. It also includes support for previous/next controls and indicators.
 * Please be aware that nested Carousels are not supported, and Carousels are generally not compliant with accessibility standards.
 *
 */
@Component({
  tag: 'fluid-carousel',
  styleUrl: 'fluid-carousel.scss',
  shadow: true,
})
export class FluidCarousel implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element
   */
  @Element() host: HTMLFluidCarouselElement;

  // ================================================================ //
  // -- States

  /**
   * Array of HTMLElement references to slotted "slide" children.
   */
  @State() _slides: HTMLElement[] = [];

  /**
   * Reference to currently "active" (i.e. visible) child slide.
   */
  @State() _activeSlide: HTMLElement = null;

  /**
   * A mutable property that holds the identifier of the interval set by `setInterval`.
   * This can be used to clear the interval when it is no longer needed.
   *
   * @type {ReturnType<typeof setInterval>}
   */
  @State() _intervalId: ReturnType<typeof setInterval>;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When true, navigation controls will only be visible on Carousel
   * :hover or :focus-within.
   */
  @Prop({ mutable: true }) controlsOnHover = false;

  /**
   * Sets the active/visible slide based on "id" attribute. This Prop is
   * also dynamically updated to match the "id" attr of the currently
   * active/visible slide (if "id" exists on the slotted slide child).
   */
  @Prop({ mutable: true }) activeSlideId: string;

  /**
   * The interval in milliseconds for the carousel to automatically advance to the next slide.
   *
   * @type {number}
   */
  @Prop({ mutable: true }) interval: number;

  @Watch('activeSlideId')
  specifyActiveSlide() {
    if (
      isNullOrUndefined(this.activeSlideId) ||
      this._activeSlide?.id === this.activeSlideId
    )
      return;

    const slide = this._slides
      .filter((slide) => slide.id === this.activeSlideId)
      .pop();
    if (this.activeSlideId && slide) {
      this.setActiveSlide(slide);
    } else {
      Utils.consoleWarn(this.host, CAROUSEL_INVALID_ACTIVE_ID);
    }
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: getSlides - stores references to all slotted child elements
   * @action: specifyActiveSlide - sets active slide by "activeSlideId" Prop OR to 1st child
   * @action: updateSlides - makes active slide visible & all other slides hidden
   */
  componentWillLoad(): void {
    this.getSlides();

    if (this.activeSlideId) {
      this.specifyActiveSlide();
    } else {
      this._activeSlide = this._slides[0];
    }

    this.updateSlides(this.interval);
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * It's never called during the first render().
   * @action: updateSlides - makes active slide visible & all other slides hidden
   */
  componentWillUpdate() {
    this.updateSlides(this.interval);
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Activates the next slide in the stack, or circles back to the first
   */
  @Method() async nextSlide() {
    let nextIndex = this.getActiveSlideIndex() + 1;
    nextIndex = nextIndex >= this._slides.length ? 0 : nextIndex;
    this.setActiveSlide(this._slides[nextIndex]);
  }

  /**
   * Activates the previous slide in the stack
   */
  @Method() async previousSlide() {
    let prevIndex = this.getActiveSlideIndex() - 1;
    prevIndex = prevIndex < 0 ? this._slides.length - 1 : prevIndex;
    this.setActiveSlide(this._slides[prevIndex]);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates styles of each slide - makes the "active" slide visible &
   * sets all other slides to "display: none". Also updates the
   * "activeSlideId" Prop to match the "id" attr of the "active" slide,
   * or sets to undefined if no "id" attr exists on "active" slide.
   */
  updateSlides(interval?: number) {
    if (!this._slides?.length) return;

    this._slides.forEach((slide) => {
      if (slide.dataset.id === this._activeSlide?.dataset.id) {
        slide.style.display = '';
        this.activeSlideId = this._activeSlide.id || undefined;
      } else {
        slide.style.display = 'none';
      }
    });

    // If an interval is provided, set up a timer to call nextSlide
    if (interval) {
      if (this._intervalId) {
        clearInterval(this._intervalId); // Clear any existing interval
      }
      this._intervalId = setInterval(() => {
        this.nextSlide();
      }, interval);
    }
  }

  /**
   * Ensures the carousel has elements and that each element has an unique id
   */
  getSlides() {
    this._slides = Array.from(this.host?.children).map((slide: HTMLElement) => {
      slide.dataset.id = generateUniqueId();
      return slide;
    });
    this.validateSlides();
  }

  /**
   * Ensures we have elements to add to the carousel, otherwise, throw
   * a warning.
   */
  validateSlides() {
    if (this._slides.length === 0) {
      Utils.consoleWarn(this.host, CAROUSEL_NO_ELEMENTS);
    }
  }

  getActiveSlideIndex() {
    return this._slides.indexOf(
      this._slides
        .filter((slide) => {
          return slide.dataset.id === this._activeSlide.dataset.id;
        })
        .pop()
    );
  }

  /**
   * Sets the active slide.
   * @param activeSlide
   */
  setActiveSlide(activeSlide: HTMLElement) {
    this._activeSlide = activeSlide;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const props = {
      slides: this._slides,
      activeSlide: this._activeSlide,
      setActiveSlide: (slide: HTMLElement) => this.setActiveSlide(slide),
      previousSlide: () => this.previousSlide(),
      nextSlide: () => this.nextSlide(),
      theme: this.theme,
    } as FluidCarouselProps;

    return (
      <div
        tabIndex={this.controlsOnHover ? 0 : null}
        class={{
          [`fluid-carousel theme-${this.theme}`]: true,
          'controls-on-hover': this.controlsOnHover,
        }}>
        <slot />
        <CarouselNavigation {...props} />
      </div>
    );
  }
}
